/* eslint-disable max-lines */
import React, { useState, useEffect, useMemo } from 'react';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { setCurrentBooking } from '@redux/booking';
import { bookingSelector } from '@redux/booking/selectors';
import { colorsSelector } from '@redux/colorPalette/selectors';
import { bamIdSelector, roomCountrySelector } from '@redux/room/selectors';
import { bamSessionUserSelector, sessionEndTimestampSelector } from '@redux/session/selectors';
import useSelector from '@redux/useSelector';
import analytics from 'lib/analytics/analytics';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux/es/hooks/useDispatch';

import { QUARTER_HOUR_TO_HOUR_RATIO } from 'constants/numbers';
import { extensionPrices } from 'services/helpers/extensionPrice';
import { getAvailableNextSlot } from 'services/helpers/getAvailableNextSlot';
import { extendBooking, getRoomSlots, sendOrderToLightSpeed } from 'services/networking/booking';
import { Slot } from 'shared-types/roomSlots';

import {
  BamFormControlLabel,
  BamRadioGroup,
  FieldSetContent,
  FieldSetContentLabelPrice,
  FieldSetContentLabelTime,
  PinkRadio,
  ProlongationButton,
  ProlongationButtonContainer,
} from './ProlongationHandlerStyle';
import dayjs from 'dayjs';
import { ModalExtensionTitleStyle } from './ProlongationHandlerContainer.style';

export const ProlongationHandler: React.FC<{
  onSuccess: (newEndSessionTime: number) => void;
}> = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const bamRoomId = useSelector(bamIdSelector);
  const user = useSelector(bamSessionUserSelector);
  const booking = useSelector(bookingSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [nextSlotAvailable, setNextSlotAvailable] = useState<Slot | null>(null);
  const [selectedCount, setSelectedCount] = useState<null | number>(null);
  const country = useSelector(roomCountrySelector);
  const colors = useSelector(colorsSelector);
  const endTimeMs = useSelector(sessionEndTimestampSelector);
  const [confirmationState, setConfirmationState] = useState(false);

  if (!bamRoomId) {
    return null;
  }

  const toggleConfirmationState = () => setConfirmationState(!confirmationState);

  const extensionPrice = useMemo(() => {
    if (!nextSlotAvailable || !booking?.capacity || !selectedCount) {
      return 0;
    }

    const pricePerHour = booking.capacity * nextSlotAvailable.pricePerPerson;
    return pricePerHour * QUARTER_HOUR_TO_HOUR_RATIO * selectedCount;
  }, [nextSlotAvailable, selectedCount]);

  const extensionDuration = useMemo(() => {
    if (!selectedCount) {
      return 0;
    }

    return selectedCount * 15;
  }, [selectedCount]);

  const onProlongationSubmit = async (selectedCount: number | null) => {
    setIsLoading(true);

    if (!selectedCount || !nextSlotAvailable || !endTimeMs || !booking) return;

    const newBooking = await extendBooking(booking.bookingId, selectedCount * 15, extensionPrice);
    if (!newBooking || !user?.bamCustomerId) {
      setNextSlotAvailable(null);
      setIsLoading(false);
      return;
    }

    dispatch(
      setCurrentBooking({
        ...booking,
        bookingId: newBooking.id,
        endHour: newBooking.endHour,
        bookingPrincipalId: booking.bookingId,
      }),
    );

    await sendOrderToLightSpeed({
      customerId: user?.bamCustomerId,
      price: extensionPrice,
      quantity: 1,
      roomId: bamRoomId,
    });

    analytics.track('prolongation_succeed', {
      customerId: user?.bamCustomerId,
      price: extensionPrice,
      selectedCount,
    });

    onSuccess(endTimeMs + selectedCount * 15 * 60 * 1000);
  };

  useEffect(() => {
    if (selectedCount) analytics.track('prolongation_extra_time_selection', { selectedCount });
  }, [selectedCount]);

  const searchAvaibleSlot = async () => {
    const roomSlot = await getRoomSlots(bamRoomId);
    if (!roomSlot.length || !booking) {
      setIsLoading(false);
      return;
    }
    const nextSlot = await getAvailableNextSlot(booking, roomSlot[0]);

    setIsLoading(false);
    setNextSlotAvailable(nextSlot);
  };

  useEffect(() => {
    searchAvaibleSlot();
  }, []);

  return (
    <>
      {isLoading ? (
        <CircularProgress color='inherit' />
      ) : (
        <>
          {nextSlotAvailable && booking ? (
            confirmationState ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '300px', gap: '15px' }}>
                <Typography sx={ModalExtensionTitleStyle}>
                  <FormattedMessage
                    id='home.prolongation.confirmation-title'
                    values={{
                      time: dayjs({ minutes: extensionDuration }).format(
                        extensionDuration < 60 ? 'mm [min]' : 'H[h]mm',
                      ),
                      price:
                        country === 'ENG' ? `£${extensionPrice / 100}` : `${extensionPrice / 100}€`,
                    }}
                  />
                </Typography>

                <ProlongationButton
                  background={colors.buttonSecondary}
                  onClick={() => onProlongationSubmit(selectedCount)}
                >
                  <FormattedMessage id='actions.confirm' />
                </ProlongationButton>
                <ProlongationButton
                  background={colors.confirmationSnackbarBackground}
                  onClick={() => toggleConfirmationState()}
                >
                  <FormattedMessage id='actions.cancel' />
                </ProlongationButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '25px',
                  width: '100%',
                }}
              >
                <Typography sx={ModalExtensionTitleStyle}>
                  <FormattedMessage id='home.prolongation.title' />
                </Typography>

                <BamRadioGroup name='myRadioGroup'>
                  {extensionPrices(nextSlotAvailable, booking)
                    .reverse()
                    .map(({ price, time, minutes }) => (
                      <BamFormControlLabel
                        key={time.toString()}
                        onClick={() => setSelectedCount(time)}
                        display={'flex'}
                        value={time}
                        control={<PinkRadio checked={selectedCount === time} colors={colors} />}
                        labelPlacement={'start'}
                        label={
                          <FieldSetContent isSelected={selectedCount === time} colors={colors}>
                            <FieldSetContentLabelTime colors={colors}>
                              {dayjs({ minutes }).format(minutes < 60 ? 'mm [min]' : 'H[h]mm')}
                            </FieldSetContentLabelTime>
                            <FieldSetContentLabelPrice colors={colors}>
                              {country === 'ENG' ? `£${price}` : `${price}€`}
                            </FieldSetContentLabelPrice>
                          </FieldSetContent>
                        }
                      />
                    ))}
                </BamRadioGroup>
                <ProlongationButtonContainer>
                  <ProlongationButton
                    background={colors.buttonSecondary}
                    onClick={() => toggleConfirmationState()}
                  >
                    <FormattedMessage id='home.prolongation.request-prolongation' />
                  </ProlongationButton>
                </ProlongationButtonContainer>
              </Box>
            )
          ) : (
            <Stack justifyContent='center' alignItems='center' gap={4} width='100%' px={4}>
              <Typography fontWeight={700} fontSize={20} textAlign='center'>
                <FormattedMessage id='home.prolongation.no-slot-available.title' />
              </Typography>

              <Typography textAlign='center' fontSize={14}>
                <FormattedMessage id='home.prolongation.no-slot-available.message' />
              </Typography>

              <Typography fontWeight={700} textAlign='center' fontSize={14}>
                <FormattedMessage id='home.prolongation.no-slot-available.call-staff' />
              </Typography>
            </Stack>
          )}
        </>
      )}
    </>
  );
};

export default ProlongationHandler;
